// Pure renderer: turns a ClaimProjection into a plain-text transcript that a
// human (or an LLM) can eyeball alongside the product mechanics. Engineering-
// internal review tool — NOT a customer-facing export. See
// `docs/calculation-projection-prompt.md` for the rationale.
//
// The five sections (header / returns / year snapshots / months / brief) mirror
// the on-page projection-details view so a reviewer can cross-check the
// transcript against the UI and against the product brief.

import { CareEnvironment, CarePhase } from '../../../../appModel';
import { unlimitedMaxBenefitMagicNumber } from '../sharedConstants';
import {
  getMonthPayout,
  getRemainingDeathBenefit,
  getTotalIndemnitySurplus,
  getTotalIneligibleCost,
  getTotalLtcCoveragePayout,
  getTotalNonLtcPayout,
  getTotalPayout,
  getTotalProjectedCareCost,
} from '../projectionTotals';
import {
  currency,
  currencyCents,
  delta,
  fixedWidthTable,
  labelledAmounts,
  pct,
  TranscriptColumn,
} from './formatters';

export interface TranscriptOptions {
  // Human-friendly strategy label for the title (e.g. "CareMatters II"). Falls
  // back to `terms.strategyId`.
  strategyDisplayName?: string;
  // The hand-written product brief for this strategy, appended verbatim as the
  // final section. Resolve it from `briefs/` keyed off `terms.strategyId`.
  brief?: string;
}

type Claimant = NonNullable<MonthContext['claimant']>;

export function renderTranscript(
  projection: ClaimProjection,
  options: TranscriptOptions = {},
): string {
  const { terms, yearSnapshots, months } = projection;
  const isJoint = months.some(m => m.context.claimant != null);

  const sections = [
    renderTitle(terms, options),
    renderHeaderBlock(terms),
    renderReturnsSummary(months),
    renderYearSnapshots(yearSnapshots),
    ...renderMonthSections(months, isJoint),
  ];

  if (options.brief?.trim()) {
    sections.push(`## Product brief\n${options.brief.trim()}`);
  }

  return sections.join('\n\n') + '\n';
}

// Title deliberately omits any client identifier — the transcript is meant to
// be pasted into external review tools, so it must stay free of PII/PHI. Only
// the (non-identifying) product strategy is named.
function renderTitle(terms: ClaimTerms, options: TranscriptOptions): string {
  const strategy = options.strategyDisplayName ?? terms.strategyId;
  return `# Projection — ${strategy}`;
}

function renderHeaderBlock(terms: ClaimTerms): string {
  const lines: string[] = [];

  const riderDaily = terms.nominalDailyBenefit - terms.nominalDailyBenefitBase;
  lines.push(
    `at_issue_daily_benefit: ${currencyCents(terms.nominalDailyBenefit)} ` +
      `(base ${currencyCents(terms.nominalDailyBenefitBase)} / rider ${currencyCents(riderDaily)})`,
  );

  const inflationMode = terms.inflationIsSimple ? 'simple' : 'compound';
  lines.push(
    `inflation_rate: ${pct(terms.inflationRate)} ${inflationMode}, ${capLabel(terms.inflationCapYears)}`,
  );

  if (terms.baseBenefitPoolInflationRate != null) {
    lines.push(
      `base_pool_inflation: ${pct(terms.baseBenefitPoolInflationRate)}` +
        capSuffix(terms.baseBenefitPoolInflationCapYears),
    );
  }
  if (terms.extensionOfBenefitsPoolInflationRate != null) {
    lines.push(
      `eob_pool_inflation: ${pct(terms.extensionOfBenefitsPoolInflationRate)}` +
        capSuffix(terms.extensionOfBenefitsPoolInflationCapYears),
    );
  }
  // Per-pool unlimited check: `terms.isUnlimited` means the LIFETIME pool is
  // unlimited, but on two-pool products only ONE pool may carry the sentinel
  // (e.g. unlimited COB with a finite base) — so each pool compares its own
  // at-issue value against the sentinel.
  if (terms.baseBenefitPoolAtIssue != null) {
    lines.push(`base_pool_at_issue: ${poolAmount(terms.baseBenefitPoolAtIssue)}`);
  }
  if (terms.extensionOfBenefitsPoolAtIssue != null) {
    lines.push(`eob_pool_at_issue: ${poolAmount(terms.extensionOfBenefitsPoolAtIssue)}`);
  }

  lines.push(`care_cost_inflation: ${pct(terms.careCostInflationRate)} / yr`);
  lines.push(
    `projection_anchor_year: ${terms.projectionAnchorYear}, ` +
      `first_phase_start_year: ${terms.firstPhaseStartYear}, ` +
      `policy_premium_start_year: ${terms.policyPremiumStartYear}`,
  );
  lines.push(`minimum_guaranteed_death_benefit: ${currency(terms.minimumGuaranteedDeathBenefit)}`);
  lines.push(`payout_model: ${payoutModel(terms)}`);
  lines.push(`strategy_id: ${terms.strategyId}`);

  // Stale/inconsistent-data warnings raised by the strategy (the "block +
  // warn" pattern). Surfaced prominently so a transcript reviewer knows the
  // row was kept on a legacy interpretation.
  for (const warning of terms.dataWarnings ?? []) {
    lines.push(`DATA WARNING: ${warning}`);
  }

  return lines.join('\n');
}

function capLabel(capYears: number): string {
  return capYears > 0 ? `capped at ${capYears} yr` : 'uncapped';
}

function capSuffix(capYears: number | undefined): string {
  return capYears != null && capYears > 0 ? `, capped at ${capYears} yr` : ', uncapped';
}

// Pool amounts at/above the unlimited sentinel render as "Unlimited" so the
// magic dollar figure never leaks into the transcript.
function poolAmount(value: number): string {
  return value >= unlimitedMaxBenefitMagicNumber ? 'Unlimited' : currency(value);
}

// Running pool balance with its monthly delta; an unlimited pool has no
// meaningful balance or delta, so it collapses to the bare "Unlimited" tag.
function poolBalance(value: number, change: number): string {
  return value >= unlimitedMaxBenefitMagicNumber
    ? 'Unlimited'
    : `${currency(value)} ${delta(change)}`;
}

function payoutModel(terms: ClaimTerms): string {
  if (terms.isIndemnity) return 'indemnity';
  if (terms.isReimbursement) return 'reimbursement';
  return 'unknown';
}

function renderReturnsSummary(months: MonthlyEntry[]): string {
  const rows = [
    { label: 'LTC payout', value: currency(getTotalLtcCoveragePayout(months)) },
    { label: 'Indemnity surplus', value: currency(getTotalIndemnitySurplus(months)) },
    { label: 'Remaining death benefit', value: currency(getRemainingDeathBenefit(months)) },
    { label: 'Non-LTC payout', value: currency(getTotalNonLtcPayout(months)) },
    { label: 'Total payout', value: currency(getTotalPayout(months)) },
    { label: 'Total projected LTC cost', value: currency(getTotalProjectedCareCost(months)) },
    { label: 'Ineligible cost (OOP)', value: currency(getTotalIneligibleCost(months)) },
  ];
  return `## Returns summary\n${labelledAmounts(rows)}`;
}

// In-claim snapshots: `claim_yr+` is the claim-relative year offset
// (floor(monthIndex / 12)), NOT an offset from `projection_anchor_year` —
// accumulation rows carry `yearsFromAnchor` for that.
function renderYearSnapshots(snapshots: YearSnapshot[]): string {
  const columns: TranscriptColumn[] = [
    { header: 'yr', align: 'right' },
    { header: 'claim_yr+', align: 'right' },
    { header: 'daily_base', align: 'right' },
    { header: 'daily_rider', align: 'right' },
    { header: 'daily_eff', align: 'right' },
    { header: 'monthly_cap', align: 'right' },
    { header: 'pool_ceiling', align: 'right' },
  ];
  const rows = snapshots.map(s => [
    String(s.policyYear),
    s.claimYearOffset != null ? String(s.claimYearOffset) : '—',
    currencyCents(s.effectiveDailyBenefitBase),
    currencyCents(s.effectiveDailyBenefitRider),
    currencyCents(s.effectiveDailyBenefit),
    currency(s.monthlyBenefitCap),
    poolAmount(s.theoreticalLtcPoolCeiling),
  ]);
  return `## Year snapshots\n${fixedWidthTable(columns, rows)}`;
}

function renderMonthSections(months: MonthlyEntry[], isJoint: boolean): string[] {
  if (!isJoint) {
    return [`## Months\n${renderMonthTable(months, false)}`];
  }

  // Joint tables are titled by the (non-identifying) claimant role tag, never a
  // client name — see `renderTitle` on the PII/PHI constraint.
  const claimants: Claimant[] = ['client', 'partner'];
  return claimants
    .map(claimant => {
      const claimantMonths = months.filter(m => m.context.claimant === claimant);
      if (claimantMonths.length === 0) return null;
      return `## Months — ${claimant}\n${renderMonthTable(claimantMonths, false)}`;
    })
    .filter((s): s is string => s != null);
}

function renderMonthTable(months: MonthlyEntry[], includeClaimant: boolean): string {
  const columns: TranscriptColumn[] = [
    { header: 'idx', align: 'right' },
    { header: 'yr', align: 'right' },
    { header: 'age', align: 'right' },
    ...(includeClaimant ? [{ header: 'claimant', align: 'left' } as TranscriptColumn] : []),
    { header: 'ph/env', align: 'left' },
    { header: 'care_cost', align: 'right' },
    { header: 'days', align: 'left' },
    { header: 'cap', align: 'left' },
    { header: 'payout', align: 'right' },
    { header: 'payout_detail', align: 'left' },
    { header: 'ineligible', align: 'right' },
    { header: 'base_pool', align: 'right' },
    { header: 'eob', align: 'right' },
    { header: 'db', align: 'right' },
    { header: 'pool_flows', align: 'left' },
    { header: 'strategy', align: 'left' },
    { header: 'out_of_pocket', align: 'left' },
    { header: 'milestones', align: 'left' },
  ];

  const rows = months.map(month => {
    const { context, facts, openState, state, deltas, events } = month;
    const basePool = state.pools.baseBenefitPool;
    const baseDelta = basePool - openState.pools.baseBenefitPool;
    const eobPool = state.pools.extensionOfBenefitsPool;
    const eobDelta = eobPool - openState.pools.extensionOfBenefitsPool;
    const db = state.pools.deathBenefit;
    const dbDelta = db - openState.pools.deathBenefit;

    return [
      String(context.monthIndex),
      String(context.policyYear),
      context.clientAge != null ? String(context.clientAge) : '—',
      ...(includeClaimant ? [context.claimant ?? '—'] : []),
      `${phaseLabel(context.carePhase)}/${envLabel(context.careEnvironment)}`,
      currency(facts.effectiveCareCost),
      renderDayFacts(facts),
      renderAppliedCap(facts),
      currency(getMonthPayout(month)),
      renderPayoutDetail(month),
      currency(deltas.payout.ineligibleCost),
      poolBalance(basePool, baseDelta),
      poolBalance(eobPool, eobDelta),
      `${currency(db)} ${delta(dbDelta)}`,
      renderPoolFlows(month),
      renderStrategyDetails(month),
      renderOutOfPocket(events),
      renderMilestones(events),
    ];
  });

  return fixedWidthTable(columns, rows);
}

function renderDayFacts(facts: MonthlyCareFacts): string {
  const parts = [`elig ${days(facts.coverageEligibleDays)}`];
  if (facts.non2AdlDays > 0) parts.push(`<2 ${days(facts.non2AdlDays)}`);
  const wpDays = Math.max(
    facts.waitingPeriodDaysConsumedHome,
    facts.waitingPeriodDaysConsumedFacility,
  );
  if (wpDays > 0) {
    const homeDays = facts.waitingPeriodDaysConsumedHome;
    const facilityDays = facts.waitingPeriodDaysConsumedFacility;
    const bucketDetail =
      homeDays > 0 && facilityDays > 0 && homeDays !== facilityDays
        ? ` (home ${days(homeDays)}, fac ${days(facilityDays)})`
        : '';
    parts.push(`wp ${days(wpDays)}${bucketDetail}`);
  }
  return parts.join('; ');
}

function renderAppliedCap(facts: MonthlyCareFacts): string {
  if (facts.appliedMonthlyBenefitCap === 0) return '—';
  return `${currency(facts.appliedMonthlyBenefitCap)} ${facts.appliedCapPoolSource}`;
}

function renderPayoutDetail(month: MonthlyEntry): string {
  const { payout, strategyDeltas } = month.deltas;
  const parts: string[] = [];

  if (payout.ltcCoveragePaid > 0) parts.push(`coverage ${currency(payout.ltcCoveragePaid)}`);
  if (payout.ltcPayoutToCost > 0 && payout.ltcPayoutToCost !== payout.ltcCoveragePaid) {
    parts.push(`to_cost ${currency(payout.ltcPayoutToCost)}`);
  }
  if (payout.indemnityPayout > 0) parts.push(`indemnity ${currency(payout.indemnityPayout)}`);

  const epLump = readNumber(strategyDeltas, 'epLumpSumPaid');
  if (epLump > 0) parts.push(`ep_lump ${currency(epLump)}`);

  return parts.length > 0 ? parts.join('; ') : '—';
}

function renderPoolFlows(month: MonthlyEntry): string {
  const parts = [
    renderPoolFlow('base', month.deltas.base),
    renderPoolFlow('eob', month.deltas.eob),
    renderDeathBenefitFlow(month.deltas.deathBenefit),
  ].filter((s): s is string => s != null);

  return parts.length > 0 ? parts.join('; ') : '—';
}

function renderPoolFlow(label: string, flow: PoolFlow): string | null {
  const parts: string[] = [];
  if (flow.inflationAccrual > 0) parts.push(`+${currency(flow.inflationAccrual)} infl`);
  if (flow.depletion > 0) parts.push(`-${currency(flow.depletion)} draw`);
  return parts.length > 0 ? `${label} ${parts.join(' ')}` : null;
}

function renderDeathBenefitFlow(flow: DeathBenefitFlow): string | null {
  if (flow.reduction === 0 && !flow.flooredAtMinimum) return null;
  const parts: string[] = [];
  if (flow.reduction > 0) parts.push(`-${currency(flow.reduction)}`);
  if (flow.flooredAtMinimum) parts.push('floor');
  return `db ${parts.join(' ')}`;
}

function renderStrategyDetails(month: MonthlyEntry): string {
  const parts = [
    ...renderKnownStrategyDeltas(month.deltas.strategyDeltas),
    ...renderKnownStrategyState(month.state.strategyState),
    ...renderUnknownStrategySlot(month.deltas.strategyDeltas),
  ];

  return parts.length > 0 ? parts.join('; ') : '—';
}

function renderKnownStrategyDeltas(slot: unknown): string[] {
  const parts: string[] = [];

  addMoney(parts, 'base_paid', readNumber(slot, 'baseBenefitPaid'));
  addMoney(parts, 'rider_paid', readNumber(slot, 'riderBenefitPaid'));
  addDays(parts, 'base_days', readNumber(slot, 'baseBenefitDays'));
  addDays(parts, 'rider_days', readNumber(slot, 'riderBenefitDays'));
  addDays(parts, 'cob_days', readNumber(slot, 'cobBenefitDays'));
  addMoney(parts, 'base_limit', readNumber(slot, 'baseMonthlyLimit'));
  addMoney(parts, 'cob_limit', readNumber(slot, 'cobMonthlyLimit'));
  addMoney(parts, 'av_reduction', readNumber(slot, 'accountValueReduction'));
  addMoney(parts, 'surplus_accrued', readNumber(slot, 'indemnitySurplusAccrued'));
  addMoney(parts, 'surplus_applied', readNumber(slot, 'indemnitySurplusApplied'));
  addMoney(parts, 'ep_lump', readNumber(slot, 'epLumpSumPaid'));
  addMoney(parts, 'ep_base', readNumber(slot, 'epLumpSumBasePaid'));
  addDays(parts, 'ep_days', readNumber(slot, 'epLumpSumDays'));
  addMoney(parts, 'ep_daily', readNumber(slot, 'epLumpSumDailyRate'));
  addMoney(parts, 'ep_to_cost', readNumber(slot, 'epLumpSumAppliedToCost'));
  addDays(parts, 'period_used', readNumber(slot, 'benefitPeriodDaysConsumed'));
  addMoney(parts, 'cv_interest', readNumber(slot, 'contractValueInterestCredit'));
  addMoney(parts, 'cv_charge', readNumber(slot, 'contractValueRiderCharge'));
  addMoney(parts, 'accelerated_draw', readNumber(slot, 'acceleratedBenefitDraw'));
  addMoney(parts, 'extended_draw', readNumber(slot, 'extendedBenefitDraw'));

  return parts;
}

function renderKnownStrategyState(slot: unknown): string[] {
  const parts: string[] = [];

  addMoney(parts, 'surplus_bal', readNumber(slot, 'indemnitySurplus'));
  addBoolean(parts, 'ep_reimbursed', readBoolean(slot, 'epReimbursed'));
  addDays(parts, 'period_rem', readNumber(slot, 'benefitPeriodDaysRemaining'));

  return parts;
}

const KNOWN_STRATEGY_KEYS = new Set([
  'baseBenefitPaid',
  'riderBenefitPaid',
  'baseBenefitDays',
  'riderBenefitDays',
  'cobBenefitDays',
  'baseMonthlyLimit',
  'cobMonthlyLimit',
  'accountValueReduction',
  'indemnitySurplusAccrued',
  'indemnitySurplusApplied',
  'epLumpSumPaid',
  'epLumpSumBasePaid',
  'epLumpSumDays',
  'epLumpSumDailyRate',
  'epLumpSumAppliedToCost',
  'benefitPeriodDaysConsumed',
  'contractValueInterestCredit',
  'contractValueRiderCharge',
  'acceleratedBenefitDraw',
  'extendedBenefitDraw',
]);

function renderUnknownStrategySlot(slot: unknown): string[] {
  if (!isRecord(slot)) return [];

  return Object.entries(slot)
    .filter(([key, value]) => {
      if (KNOWN_STRATEGY_KEYS.has(key)) return false;
      if (typeof value === 'number') return value !== 0;
      if (typeof value === 'boolean') return value;
      return typeof value === 'string' && value.length > 0;
    })
    .map(([key, value]) => `${snakeKey(key)} ${formatStrategyValue(value)}`);
}

function addMoney(parts: string[], label: string, value: number): void {
  if (value !== 0) parts.push(`${label} ${currency(value)}`);
}

function addDays(parts: string[], label: string, value: number): void {
  if (value !== 0) parts.push(`${label} ${days(value)}`);
}

function addNumber(parts: string[], label: string, value: number): void {
  if (value !== 0) parts.push(`${label} ${compactNumber(value)}`);
}

function addBoolean(parts: string[], label: string, value: boolean): void {
  if (value) parts.push(`${label} yes`);
}

function readNumber(slot: unknown, key: string): number {
  if (!isRecord(slot)) return 0;
  const value = slot[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function readBoolean(slot: unknown, key: string): boolean {
  if (!isRecord(slot)) return false;
  return slot[key] === true;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function days(value: number): string {
  return `${compactNumber(value)}d`;
}

function compactNumber(value: number): string {
  return Number.isInteger(value)
    ? String(value)
    : value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function snakeKey(key: string): string {
  return key.replace(/[A-Z]/g, match => `_${match.toLowerCase()}`);
}

function formatStrategyValue(value: unknown): string {
  if (typeof value === 'number') return compactNumber(value);
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return String(value);
}

// Numeric enums reverse-map value → key name (e.g. 2802 → 'moderateCare'),
// giving readable phase/environment labels without depending on client-side
// label maps. Falls back to the raw value if the reverse lookup misses.
function phaseLabel(phase: CarePhase): string {
  return CarePhase[phase] ?? String(phase);
}

function envLabel(env: CareEnvironment): string {
  return CareEnvironment[env] ?? String(env);
}

// Milestones carry a human-readable `label` ("LTC begins at age 65", "WP
// Satisfied", "LTC pool exhausted", phase/env transitions). Emit both the stable
// `kind` token (which the product briefs reference, e.g. \`ep-lump\`) and the
// label so a reviewer can tie a row to the brief and read what happened.
function renderMilestones(events: MonthlyEvents): string {
  return events.milestones.map(m => `${m.kind}: ${m.label}`).join('; ');
}

// Out-of-pocket day buckets for the row. A single month can STACK reasons — a
// transition month may begin under the 2-ADL gate and end serving the waiting
// period — so render every non-zero bucket, not just the primary reason. Order
// matches the calc's gate-resolution priority so the stack reads chronologically.
// Reason tokens match the briefs' vocabulary (lt-2-adl / in-wp / etc.).
const OUT_OF_POCKET_BUCKETS: Array<{
  reason: ProjectionIneligibilityReason;
  key: keyof IneligibleDayBreakdown;
}> = [
  { reason: 'lt-2-adl', key: 'lt2AdlDays' },
  { reason: 'in-wp', key: 'inWpDays' },
  { reason: 'il-not-covered', key: 'ilNotCoveredDays' },
  { reason: 'pre-policy', key: 'prePolicyDays' },
];

function renderOutOfPocket(events: MonthlyEvents): string {
  const { ineligibleDayBreakdown: breakdown } = events;
  return OUT_OF_POCKET_BUCKETS.filter(b => breakdown[b.key] > 0)
    .map(b => `${b.reason} ${breakdown[b.key]}d`)
    .join(' + ');
}
